<template>
  <div>
    <p v-if="name"
      :class="{isRequired: isRequired}"
      class="optionLabel">
      {{ name[lang] }}
      <span v-if="quantityMessage" class="notranslate">({{ quantityMessage }})</span>
      <span v-if="isRequired && !isValid" class="requiredStkr notranslate">{{ locale.required }}</span>
    </p>
    <div :class="{activeSubOption: !isSubOption && getActiveSubOption}">
      <div
        v-for="(opt, index) in optionItems"
        :key="index"
        :class="{selected: selected.includes(opt.optionItemId), isDisabled: disabledCheck(opt.optionItemId) || opt.status}"
        class="optionItem">
        <span
          class="checkbox"
          :class="{disabled: disabledCheck(opt.optionItemId) || opt.status}">
          <span v-if="opt.status" class="status">
            {{ getItemStatus(opt.status) }}
          </span>
          <input
            type="checkbox"
            :name="`${uniqueId}_option`"
            :id="opt.optionItemId"
            :disabled="disabledCheck(opt.optionItemId) || opt.status"
            :value="opt.optionItemId"
            :checked="selected.includes(opt.optionItemId) && !opt.status"
            @change="setCheck(opt.optionItemId)" />
          <label
            :for="opt.optionItemId">
            {{ getOptionName(opt) }}
            <span
              v-if="opt.price && opt.price > 0"
              class="optionItemPrice notranslate">
              {{ priceFormat(opt.price) }}
            </span>
          </label>
        </span>
        <span v-if="max > 1 && opt.max > 1" class="quantity notranslate">
          <button
            type="button"
            class="btnMinus"
            :disabled="!selected.includes(opt.optionItemId)"
            @click.stop="decrease(opt.optionItemId)">
          </button>
          <span class="qty">{{ opt.quantity }}{{ locale.unit_item }}</span>
          <button
            type="button"
            class="btnPlus"
            :disabled="!selected.includes(opt.optionItemId)"
            @click.stop="increase(opt.optionItemId)">
          </button>
        </span>
      </div>
    </div>
    <div v-if="getActiveSubOption && subOption && subOption.subOption" class="shopSubOption">
      <div
        v-for="(opt, idx) in subOption.subOption"
        :key="idx">
        <ItemOption
          class="shopOptionItem isActive"
          :locale="locale"
          :lang="lang"
          :shopResponsive="shopResponsive"
          :uniqueId="opt.uniqueId"
          :shopItemId="shopItemId"
          :option="opt"
          :isSubOption="true" />
      </div>
    </div>
  </div>
</template>

<script>
import regExp from '@/common/regExp'

export default {
  name: 'OptionCheckbox',
  props: [
    'locale',
    'lang',
    'shopItemId',
    'uniqueId',
    'shopResponsive',
    'optionInfo',
    'isSubOption',
    'cartOptionData'
  ],
  components: {
    ItemOption: () => import('../ItemOption.vue')
  },
  computed: {
    isStore () {
      return this.$route.name === 'store'
    },
    siteCommon () {
      return this.$store.getters['common/getSetting']
    },
    quantityMessage () {
      const min = this.min
      const max = this.max
      return  min === max
        ? this.locale.shop_condition_quantity_specific.replace('{var}', max) // N개 선택
        : this.locale.shop_condition_quantity_range.replace('{min}', min).replace('{max}', max) // N~M개 선택
    },
    totalQuantity () {
      // 선택항목뿐 아니라 선택한 항목의 개수의 총합을 max로 계산함
      const optionItems = this.optionItems.filter(x => this.selected.includes(x.optionItemId))
      return optionItems.reduce((a, b) => a + b.quantity, 0)
    },
    itemProperty () {
      const shopItem = this.$store.getters['shop/getItemProperty'](this.shopItemId)
      return shopItem
    },
    selectedItems () {
      const code = this.siteCommon.currency.toUpperCase()
      const itemProperty = this.itemProperty
      const isReplaceOptionPrice = itemProperty && itemProperty.replaceOptionPrice
      function mergeOptionItemNames(itemNameObj = {}, optionItemNameObj = {}) {
        const allKeys = new Set([
          ...Object.keys(itemNameObj),
          ...Object.keys(optionItemNameObj)
        ]);

        const result = {};

        allKeys.forEach(key => {
          const item = itemNameObj[key] || '';
          const option = optionItemNameObj[key] || '';

          if (item && option) {
            result[key] = `${item} ${option}`;
          } else if (item) {
            result[key] = item;
          } else if (option) {
            result[key] = `${option}`;
          } else {
            result[key] = ''; // 둘 다 비었으면 빈 문자열
          }
        });

        return result;
      }
      return this.optionItems.filter(item => !item.status && this.selected.includes(item.optionItemId)).map(item => {
        return {
          optionItemId: item.optionItemId,
          name: isReplaceOptionPrice ? mergeOptionItemNames(itemProperty.shopItemName, item.name) : item.name,
          quantity: item.quantity,
          ...item.shopItemId && {
            shopItemId: item.shopItemId
          },
          ...item.price && {
            price: item.price,
            totalPrice: regExp.priceTotal(item.price, item.quantity, code)
          },
          ...item.pId && {
            pId: item.pId
          }
        }
      })
    },
    isValid () {
      return this.isRequired ? this.totalQuantity >= this.min && this.totalQuantity <= this.max : true
    },
    getActiveSubOption () {
      return this.$store.getters['shop/getActiveSubOption']
    }
  },
  created () {
    this.name = this.optionInfo.name
    this.isRequired = this.optionInfo.isRequired
    this.optionItems = this.optionInfo.optionItems.map(x => {
      return {
        ...x,
        quantity: 1
      }
    })
    this.selected = this.optionInfo.selected
    this.min = this.optionInfo.min || 1
    this.max = this.optionInfo.max || 10
    if (this.cartOptionData) {
      this.optionItems = this.optionItems.map(x => {
        const cartOptionItem = this.cartOptionData.optionItems.find(o => o.optionItemId === x.optionItemId)
        return {
          ...x,
          ...cartOptionItem && {
            quantity: cartOptionItem.quantity
          }
        }
      })
      this.selected = this.cartOptionData.optionItems.map(x => x.optionItemId)
    }
  },
  watch: {
    selectedItems: {
      handler (selectedItems) {
        const data = {
          uniqueId: this.uniqueId,
          isValid: this.isValid,
          optionItems: selectedItems
        }
        this.$store.commit('shop/setDetailOptions', [data])
      },
      deep: true
    }
  },
  data () {
    return {
      name: {},
      isRequired: false,
      optionItems: [],
      min: 1,
      max: 10,
      quantity: 1,
      selected: [],
      subOption: null
    }
  },
  methods: {
    getItemStatus (presetId) {
      const getStatus = this.$store.getters['shop/getItemStatus'](presetId)
      const status = (getStatus && getStatus.presetOptions[0]) || null
      return status && status.text && status.text[this.lang]
    },
    priceFormat (price) {
      const currency = this.$store.getters['shop/getShopCurrency'] || null
      return this.$store.getters['util/getPriceFormatter'](price, currency)
    },
    getOptionName (opt) {
      const lang = this.lang
      const optionItemName = opt.name[lang]
      const itemProperty = this.itemProperty
      const isReplaceOptionPrice = itemProperty && itemProperty.replaceOptionPrice
      const isSubOption = this.isSubOption
      return optionItemName
    },
    getPriceTotal (price, quantity, code) {
      return regExp.priceTotal(price, quantity, code)
    },
    disabledCheck (id) {
      const isChecked = this.selected.includes(id)
      const isRadio = this.min === 1 && this.max === 1
      const quantityIsMax = this.totalQuantity >= this.max
      return !isRadio && !isChecked && quantityIsMax
    },
    // 단일선택(Radio) 처리
    async setCheck(optionItemId) {
      await this.$nextTick() // DOM 업데이트 후 실행

      let subOption = null
      this.subOption = null

      // ✅ 라디오 버튼 + 토글
      if (this.min === 1 && this.max === 1) {
        this.selected = this.selected.includes(optionItemId) ? [] : [optionItemId]
      } else {
        // ✅ 체크박스 방식
        this.selected = this.selected.includes(optionItemId)
          ? this.selected.filter(item => item !== optionItemId)
          : [...this.selected, optionItemId]
      }

      // 체크해제하면 수량 초기화
      this.optionItems = this.optionItems.map(item => {
        const unchecked = !this.selected.includes(item.optionItemId)
        return {
          ...item,
          ...unchecked && {
            quantity: 1
          }
        }
      })

      // ✅ 선택된 상태일 때만 서브옵션 확인
      if (!this.isSubOption && this.selected.includes(optionItemId)) {
        const optionItem = this.optionItems && this.optionItems.find(x => x.optionItemId === optionItemId)
        const subOptionIds = optionItem && optionItem.subOption && optionItem.subOption.map(x => x.id)

        if (subOptionIds && subOptionIds.length > 0) {
          const reqSubOption = await this.$store.dispatch('shop/getStoreSubOptionItems', {
            optId: this.optionInfo.id,
            selectedOptionItemId: optionItemId,
            subIds: subOptionIds
          })

          const rawOptionItems = reqSubOption && reqSubOption.message && reqSubOption.message.optionItem

          if (rawOptionItems && typeof rawOptionItems === 'object') {
            // 옵션 스토어에 실재하는 그룹만 필터
            const validOptionItems = Object.fromEntries(
              Object.entries(rawOptionItems).filter(([key, value]) => {
                return (
                  value &&
                  typeof value === 'object' &&
                  Object.keys(value).length > 0 &&
                  value.optId
                )
              })
            )

            subOption = Object.keys(validOptionItems).length > 0 ? validOptionItems : null
          }
        }
      }

      // ✅ 서브옵션이 있으면 구조 생성 및 스토어에 커밋
      const subOptionData = subOption
        ? this.$store.getters['shop/getOptionListData'](
            this.shopPageId,
            this.shopItemId,
            Object.values(subOption),
            this.optionInfo.id,
            optionItemId
          )
        : null

      this.subOption = {
        optionId: this.optionInfo.id,
        optionItemId: optionItemId,
        subOption: subOptionData
      }

      if (subOptionData) {
        this.setDetailSubOption(subOptionData)
        this.$store.commit('shop/setActiveSubOption', `${this.optionInfo.id}_unique_${optionItemId}`)
      }
    },
    setDetailSubOption (data) {
      const subOptionData = data.map(x => {
        const isCheckbox = x.type === 'checkbox'
        const isSelect = x.type === 'select'
        return {
          ...x.id && { // 스토어의 option.id + 프리셋의 optionIds[].id
            id: x.id
          },
          ...x.optionId && { // 상품에서 직접등록한 option
            optionId: x.optionId
          },
          parentOption: data.optionId,
          name: x.name,
          uniqueId: x.uniqueId,
          isValid: !x.isRequired,
          optionItems: x.optionItems.filter((o, i) => {
            return !o.status && (o.isDefault || (isSelect && i === 0))
          }).map(o => {
            return {
              id: o.id,
              quantity: o.quantity,
              optionItemId: o.optionItemId,
              ...o.price && {
                price: o.price,
                totalPrice: o.price
              },
              ...o.pId && {
                pId: o.pId
              }
            }
          })
        }
      })
      this.$store.commit('shop/setDetailOptions', subOptionData)
    },
    increase (id) {
      const optionItem = this.optionItems.find(x => x.optionItemId === id)
      if (optionItem && (this.totalQuantity < this.max) && (optionItem.quantity < optionItem.max)) {
        optionItem.quantity += 1
      }
    },
    decrease (id) {
      const optionItem = this.optionItems.find(x => x.optionItemId === id)
      if (optionItem && optionItem.quantity > 1 ) {
        optionItem.quantity -= 1
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.optionItem {
  &.isDisabled {
    background-color: $bg-default;
    opacity: 0.8;
  }
  .status {
    display: inline-block;
    margin-bottom: 8px;
    padding: 0.2em 1em;
    vertical-align: top;
    font-size: 14px;
    color: $color-white;
    border-radius: 4px;
    background-color: $color-grey;
  }
}
</style>
